import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Toolbar, Divider } from '@mui/material';

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>{title}</Typography>
      {children}
    </Box>
  );
}

function Body({ children }) {
  return (
    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 1 }}>
      {children}
    </Typography>
  );
}

export default function Terms() {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      <Helmet>
        <title>Terms and Conditions — Turf</title>
        <meta name="description" content="Read Turf's terms and conditions governing your use of the Turf football scores and fixtures app." />
        <link rel="canonical" href="https://turf-app.io/terms" />
        <meta property="og:url" content="https://turf-app.io/terms" />
        <meta property="og:title" content="Terms and Conditions — Turf" />
        <meta property="og:description" content="Read Turf's terms and conditions governing your use of the Turf football scores and fixtures app." />
      </Helmet>
      <Toolbar />
      <Box sx={{ maxWidth: 720, mx: 'auto', px: { xs: 3, md: 4 }, py: { xs: 6, md: 8 } }}>

        <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: -1, mb: 1 }}>
          Terms and Conditions
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled', mb: 5 }}>
          Last updated: January 20, 2026
        </Typography>

        <Divider sx={{ mb: 5 }} />

        <Body>
          These Terms and Conditions ("Terms") govern your use of the Turf iOS application
          ("Turf", "we", "our", or "the app"). By downloading or using the app, you agree to be
          bound by these Terms. If you do not agree, you must not use the app.
        </Body>

        <Box sx={{ mt: 4 }}>
          <Section title="Use of the App">
            <Body>
              Turf is provided for personal, non-commercial use only. You agree to use the app in
              compliance with all applicable laws and regulations.
            </Body>
            <Body>
              You must not misuse the app, attempt to disrupt its operation, or access the app
              using automated systems or scripts.
            </Body>
          </Section>

          <Section title="Content and Data">
            <Body>
              Turf provides football scores, fixtures, results, news, and related information for
              informational purposes only. While we aim to keep information accurate and up to date,
              we do not guarantee the accuracy, completeness, or timeliness of any content.
            </Body>
            <Body>
              Match data, statistics, and news may be delayed, incomplete, or incorrect due to
              third-party data sources.
            </Body>
          </Section>

          <Section title="No Gambling or Financial Advice">
            <Body>
              Any information provided in Turf is not intended as betting, gambling, or financial
              advice. You are solely responsible for any decisions you make based on information
              shown in the app.
            </Body>
          </Section>

          <Section title="Intellectual Property">
            <Body>
              The app, including its design, text, graphics, logos, and software, is owned by or
              licensed to Turf and is protected by intellectual property laws.
            </Body>
            <Body>
              You may not copy, modify, distribute, sell, or lease any part of the app without
              prior written permission.
            </Body>
          </Section>

          <Section title="Subscriptions and Purchases">
            <Body>
              Turf may offer optional subscriptions or in-app purchases. All payments are processed
              by Apple through the App Store and are subject to Apple's terms and conditions.
            </Body>
            <Body>
              We do not have access to your payment information and cannot manage refunds. Any
              refund requests must be made through Apple.
            </Body>
          </Section>

          <Section title="Availability and Changes">
            <Body>
              We may modify, suspend, or discontinue any part of the app at any time without notice.
              We do not guarantee that the app will always be available or error-free.
            </Body>
          </Section>

          <Section title="Disclaimer of Warranties">
            <Body>
              Turf is provided "as is" and "as available", without warranties of any kind, express
              or implied, including but not limited to implied warranties of merchantability, fitness
              for a particular purpose, and non-infringement.
            </Body>
          </Section>

          <Section title="Limitation of Liability">
            <Body>
              To the maximum extent permitted by law, Turf shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages, including loss of data,
              profits, or use, arising out of or related to your use of the app.
            </Body>
            <Body>
              Our total liability for any claim relating to the app shall not exceed the amount you
              paid to use the app (if any).
            </Body>
          </Section>

          <Section title="Indemnification">
            <Body>
              You agree to indemnify and hold harmless Turf from any claims, damages, losses,
              liabilities, and expenses arising from your use of the app or violation of these Terms.
            </Body>
          </Section>

          <Section title="Governing Law">
            <Body>
              These Terms are governed by and construed in accordance with the laws of your place of
              residence, unless otherwise required by applicable law.
            </Body>
          </Section>

          <Section title="Changes to These Terms">
            <Body>
              We may update these Terms from time to time. Continued use of the app after changes
              are made constitutes acceptance of the updated Terms.
            </Body>
          </Section>

          <Section title="Contact">
            <Body>
              If you have questions about these Terms, contact us at{' '}
              <Box
                component="a"
                href="mailto:support@turf-app.io"
                sx={{ color: '#22c55e', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                support@turf-app.io
              </Box>.
            </Body>
          </Section>
        </Box>
      </Box>
    </Box>
  );
}
